import defaults from '../../translations/en.json';

/**
 * OP-32411: helpers for med orders whose Rcopia session was never closed in
 * SmartChart (e.g. the browser was refreshed while the order was pending).
 *
 * When a new pending maint status is posted for the same user + patient, the
 * clearscripts server completes the stale pending order itself (it writes the
 * medication changes into that note) but it no longer appends the boilerplate /
 * signature text; SmartChart is the source of that text and only appends it on
 * its own COMPLETE round trip. These helpers let SmartChart find such notes
 * before the new pending request and finish them afterwards.
 */

type NoteLike = {
  id: number | string;
  note?: string | null;
  personid?: string | number | null;
};

/** The sentence SmartChart writes on a med order note until Rcopia is closed. */
export const AWAITING_RCOPIA_TEXT: string = defaults.waitingRcopia;

/** True while the note still carries the "awaiting Rcopia" placeholder. */
export const isAwaitingRcopiaText = (text?: string | null): boolean =>
  !!text && text.includes(AWAITING_RCOPIA_TEXT);

/**
 * Ids of the current user's notes on this patient that are still waiting on a
 * Rcopia session, excluding the note of the order being started now.
 * The clearscripts server only auto-completes pending records for the same
 * user, so other users' pending orders are left alone.
 */
export const findPendingMedOrderNoteIds = (
  notes: NoteLike[] | null | undefined,
  currentUserId: string | number | null | undefined,
  excludeNoteId?: string | number | null,
): string[] => {
  if (!notes || currentUserId == null || currentUserId === '') {
    return [];
  }
  const userId = String(currentUserId);
  const excludeId = excludeNoteId != null ? String(excludeNoteId) : null;
  return notes
    .filter(
      (note) =>
        note &&
        note.id != null &&
        String(note.id) !== excludeId &&
        String(note.personid) === userId &&
        isAwaitingRcopiaText(note.note),
    )
    .map((note) => String(note.id));
};
